import React, { useCallback, useEffect, useMemo, useState } from "react";
import TinyEditor from "../../../custom-ckeditor/TinyEditor";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import SaveIcon from "@mui/icons-material/Save";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import ItemService from "../../../apis/ItemService";
import {
  BLOOMS_TAXONOMY_SKILLS,
  normalizeSkillTypeValue,
} from "../constants/lessonMetadata";
import { normaliseTaxonomyOptions } from "../utils/taxonomy";
import QuizPage from "../../quiz/QuizPage";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

const resolveItemFromResponse = (response) => {
  if (!response) return null;
  if (response.item) return response.item;
  if (Array.isArray(response.items)) return response.items[0] || null;
  return response;
};

const extractSentenceTokens = (sentence) =>
  typeof sentence === "string"
    ? sentence.trim().split(/\s+/).filter(Boolean)
    : [];

const createEmptyOption = (id) => ({
  id,
  Value: "",
  Rationale: "",
});

const createEmptyItem = () => ({
  Type: "ReArrange",
  Name: "",
  Title: "",
  Direction: "LTR",
  points: 1,
  skilltype: "",
  itemcat: "",
  Options: [createEmptyOption(1)],
});

const getItemIdFromAny = (res, fallbackItem) =>
  res?.item?.Id ??
  res?.item?.id ??
  res?.item?.itemId ??
  res?.Id ??
  res?.id ??
  res?.itemId ??
  fallbackItem?.Id ??
  fallbackItem?.id ??
  fallbackItem?.itemId ??
  null;

const ReArrangeItemDetail = () => {
  const { itemId } = useParams();
  const navigate = useNavigate();
  const isNew = !itemId || itemId === "new";
  const location = useLocation();
  const clonedItem = location.state?.clonedItem || null;
  const [loading, setLoading] = useState(false);
  const [item, setItem] = useState(createEmptyItem());
  const [taxonomyOptions, setTaxonomyOptions] = useState([]);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [jsonDialogOpen, setJsonDialogOpen] = useState(false);

  // 🆕 Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [selectedOptionIndex, setSelectedOptionIndex] = useState(null);
  const [previewOrder, setPreviewOrder] = useState([]);
  const [effectiveItemId, setEffectiveItemId] = useState(
    clonedItem ? null : isNew ? null : itemId
  );

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        // 1️⃣ Load taxonomy (always)
        const taxonomy = await ItemService.fetchTaxonomy("arabic");
        setTaxonomyOptions(normaliseTaxonomyOptions(taxonomy));

        let loadedItem;

        // 2️⃣ If cloning → use clonedItem and force NEW state
        if (clonedItem) {
          loadedItem = {
            ...clonedItem,

            // Remove backend-specific fields
            id: undefined,
            Id: undefined,
            itemId: undefined,
            ItemId: undefined,
            PK: undefined,
            SK: undefined,
            GPK1: undefined,
            GSK1: undefined,
            json: undefined,
            Json: undefined,

            createdBy: "",
            updatedBy: "",
            version: "v1",

            // Rename
            Name: clonedItem.Name || "Untitled",

            // Reset Options IDs
            Options: clonedItem.Options?.map((opt, idx) => ({
              id: idx + 1,
              Value: opt.Value || "",
              Rationale: opt.Rationale || "",
            })) ?? [{ id: 1, Value: "", Rationale: "" }],
          };

          setEffectiveItemId(null);
        } else if (!isNew) {
          const data = await ItemService.fetchItemById(itemId);
          const resolved = resolveItemFromResponse(data);

          loadedItem = {
            ...resolved,
            skilltype: normalizeSkillTypeValue(resolved.skilltype),
          };
        } else {
          loadedItem = createEmptyItem();
        }
        setItem(loadedItem);
      } catch (err) {
        setSaveError("Failed to load item or taxonomy.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [itemId, isNew, clonedItem]);

  useEffect(() => {
    if (clonedItem) return; // 🔥 DO NOT override clone mode

    if (isNew) {
      setEffectiveItemId(null);
    } else {
      setEffectiveItemId(itemId || null);
    }
  }, [isNew, itemId, clonedItem]);

  const directionValue =
    item.Direction?.toUpperCase() === "RTL" ? "RTL" : "LTR";
  const isRtlDirection = directionValue === "RTL";

  const handleOpenPreview = (index) => {
    const sentence = item.Options?.[index]?.Value || "";
    const tokens = extractSentenceTokens(sentence);
    setPreviewOrder(tokens);
    setSelectedOptionIndex(index);
    setPreviewDialogOpen(true);
  };

  const handleClosePreview = () => {
    setPreviewDialogOpen(false);
    setSelectedOptionIndex(null);
    setPreviewOrder([]);
  };

  const handleFieldChange = (key, value) => {
    setItem((p) => ({ ...p, [key]: value }));
  };

  const handleOptionChange = (index, field, value) => {
    setItem((prev) => {
      const opts = [...(prev.Options || [])];
      opts[index] = { ...opts[index], [field]: value };
      return { ...prev, Options: opts };
    });
  };

  const handleAddOption = () => {
    setItem((prev) => ({
      ...prev,
      Options: [...prev.Options, createEmptyOption(prev.Options.length + 1)],
    }));
  };

  const handleRemoveOption = (index) => {
    if (item.Options.length <= 1) return;
    setItem((prev) => {
      const opts = prev.Options.filter((_, i) => i !== index);
      return { ...prev, Options: opts };
    });
  };

  const handleSave = useCallback(async () => {
    if (!item.Name || !item.itemcat || !item.skilltype) {
      setSaveError("Please fill all required fields.");
      return;
    }

    const timestamp = new Date().toISOString();
    const normalizedPoints = Number(item.points) || 1;

    const payload = {
      PK: `itemcat#${item.itemcat};skilltype#${item.skilltype}`,
      SK: effectiveItemId ? `item#${effectiveItemId}` : undefined,
      createdBy: "",
      updatedBy: "",
      dateCreated: timestamp,
      dateUpdated: timestamp,
      GPK1: "item",
      GSK1: effectiveItemId ? `item#${effectiveItemId}` : undefined,

      Name: item.Name,
      itemcat: item.itemcat,
      skilltype: item.skilltype,

      Options: item.Options.map((opt, i) => ({
        id: i + 1,
        Value: opt.Value,
        Rationale: opt.Rationale || "",
      })),

      points: normalizedPoints,
      Title:
        item.Title || "<div>Arrange the words to form correct sentence</div>",
      Type: "ReArrange",
      version: "v1",
    };

    try {
      setSaving(true);

      const response = await ItemService.saveItem(
        payload,
        effectiveItemId || undefined
      );

      setSaveSuccess(true);
      setSaveError(null);

      // After creating → switch to EDIT mode
      const newId = getItemIdFromAny(response, payload);

      if (!effectiveItemId && newId) {
        setEffectiveItemId(newId);
      }
    } catch (err) {
      setSaveError(err?.message || "Failed to save item.");
    } finally {
      setSaving(false);
    }
  }, [item, effectiveItemId]);

  const handleCloneFromEditor = useCallback(() => {
    if (!item) return;

    const clonedPayload = {
      ...item,

      // 🔥 remove identifiers
      id: undefined,
      Id: undefined,
      itemId: undefined,
      ItemId: undefined,
      PK: undefined,
      SK: undefined,
      GPK1: undefined,
      GSK1: undefined,

      // metadata reset
      Name: `${item.Name || "Untitled ReArrange"} (Copy)`,
      createdBy: "",
      updatedBy: "",
      version: "v1",

      // JSON rebuilt on save
      json: undefined,
      Json: undefined,

      // 🔥 ensure clean option IDs
      Options: item.Options.map((opt, idx) => ({
        id: idx + 1,
        Value: opt.Value,
        Rationale: opt.Rationale || "",
      })),
    };

    navigate("/authoring/items/arrange/item/new", {
      state: { clonedItem: clonedPayload },
    });
  }, [item, navigate]);

  const jsonPreview = useMemo(() => JSON.stringify(item, null, 2), [item]);

  if (loading)
    return (
      <Paper sx={{ p: 3 }}>
        <LinearProgress />
        <Typography>Loading item details...</Typography>
      </Paper>
    );

  return (
    <Stack spacing={3}>
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2}>
          {/* Header */}
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
          >
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip label="ReArrange" color="secondary" />
              <Typography variant="h5" fontWeight={600}>
                {isNew ? "Create ReArrange Item" : "Edit ReArrange Item"}
              </Typography>
            </Stack>
            <Stack direction="row" spacing={1}>
              <Button
                variant="outlined"
                startIcon={<ContentCopyIcon />}
                disabled={!effectiveItemId}
                onClick={handleCloneFromEditor}
              >
                Clone
              </Button>

              <Button
                variant="outlined"
                onClick={() => setJsonDialogOpen(true)}
              >
                View JSON
              </Button>
            </Stack>
          </Stack>

          {saveSuccess && (
            <Alert severity="success">Item saved successfully!</Alert>
          )}
          {saveError && <Alert severity="error">{saveError}</Alert>}

          {/* Fields */}
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Item Name"
                fullWidth
                required
                value={item.Name}
                onChange={(e) => handleFieldChange("Name", e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="Points"
                type="number"
                fullWidth
                required
                value={item.points}
                onChange={(e) =>
                  handleFieldChange("points", Number(e.target.value))
                }
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth required>
                <InputLabel>Skill Type</InputLabel>
                <Select
                  value={item.skilltype}
                  onChange={(e) =>
                    handleFieldChange(
                      "skilltype",
                      normalizeSkillTypeValue(e.target.value)
                    )
                  }
                >
                  {BLOOMS_TAXONOMY_SKILLS.map((s) => (
                    <MenuItem key={s.value} value={s.value}>
                      {s.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth required>
                <InputLabel>Item Category</InputLabel>
                <Select
                  value={item.itemcat}
                  onChange={(e) => handleFieldChange("itemcat", e.target.value)}
                >
                  {taxonomyOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider />
          <Box>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600 }}
              gutterBottom
            >
              Instructions (Title)
            </Typography>

            <TinyEditor
              value={item.Title || ""}
              onChange={(value) => handleFieldChange("Title", value)}
              minHeight={250}
            />
          </Box>

          <Divider />
          <FormControlLabel
            control={
              <Switch
                checked={isRtlDirection}
                onChange={() =>
                  handleFieldChange("Direction", isRtlDirection ? "LTR" : "RTL")
                }
              />
            }
            label="Right-to-left direction"
          />
          <Typography variant="h6" fontWeight={600}>
            Correct Sentence Options
          </Typography>

          {item.Options.map((opt, index) => (
            <Paper
              key={opt.id}
              sx={{ p: 2, mt: 2, backgroundColor: "#fafafa" }}
            >
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography variant="subtitle1" fontWeight={600}>
                  Option {index + 1}
                </Typography>
                {item.Options.length > 1 && (
                  <IconButton
                    color="error"
                    onClick={() => handleRemoveOption(index)}
                  >
                    <DeleteIcon />
                  </IconButton>
                )}
              </Stack>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Sentence"
                value={opt.Value}
                onChange={(e) =>
                  handleOptionChange(index, "Value", e.target.value)
                }
                sx={{ mt: 1 }}
                inputProps={{
                  dir: isRtlDirection ? "rtl" : "ltr",
                  style: { textAlign: isRtlDirection ? "right" : "left" },
                }}
              />
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Rationale (optional)"
                value={opt.Rationale}
                onChange={(e) =>
                  handleOptionChange(index, "Rationale", e.target.value)
                }
                sx={{ mt: 2 }}
              />
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 1 }}>
                <Button
                  onClick={() => handleOpenPreview(index)}
                  variant="outlined"
                >
                  Preview
                </Button>
              </Box>
            </Paper>
          ))}

          <Button
            startIcon={<AddIcon />}
            onClick={handleAddOption}
            sx={{ mt: 2 }}
          >
            Add Another Option
          </Button>

          <Stack direction="row" justifyContent="flex-end">
            <Button
              startIcon={effectiveItemId ? <SaveIcon /> : <AddIcon />}
              variant="contained"
              onClick={handleSave}
              disabled={saving}
            >
              {saving
                ? effectiveItemId
                  ? "Saving..."
                  : "Creating..."
                : effectiveItemId
                ? "Save"
                : "Create"}
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {/* 🆕 Preview Dialog */}
      <Dialog
        open={previewDialogOpen}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Preview Option{" "}
          {selectedOptionIndex !== null ? selectedOptionIndex + 1 : ""}
        </DialogTitle>
        <DialogContent dividers>
          <QuizPage
            previewMode
            previewActivities={[
              {
                ...item,
                ContentId: "preview",
              },
            ]}
          />
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClosePreview}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* JSON Dialog */}
      <Dialog
        open={jsonDialogOpen}
        onClose={() => setJsonDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Item JSON Preview</DialogTitle>
        <DialogContent dividers>
          <Box
            component="pre"
            sx={{
              m: 0,
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
              fontFamily: "monospace",
              fontSize: 13,
            }}
          >
            {jsonPreview}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setJsonDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};

export default ReArrangeItemDetail;
